import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { cn } from "@/lib/utils";
import { usePaymentUpgrade, usePayment } from "@/stores/appStore";
import { MemberLabel } from "@/components/fun-ui/MemberLabel";
import { PriceBadge } from "@/components/site/PriceBadge";
import { BadgeType, PaymentRightsItem } from "@/stores/slices/commonSlice";
import { PaymentRightsList } from "./PaymentRightsList";
import { VipPointerIcon } from "../fun-ui/icons";

export type PaymentDialogUpgradeProps = {
  scale: number;
  activedIndex?: number;
  onActivedIndexChange?: (index: number) => void;
  onPaymentOptionChange: (option: { renewalDays: number, paymentPrice: number }) => void;
}

export const PaymentDialogUpgrade = ({
  scale = 1,
  activedIndex: propActivedIndex,
  onActivedIndexChange,
  onPaymentOptionChange,
}: PaymentDialogUpgradeProps) => {
  const [localActivedIndex, setLocalActivedIndex] = useState<number>(0);

  const activedIndex = propActivedIndex ?? localActivedIndex;

  const setActivedIndex = useCallback((index: number) => {
    if (onActivedIndexChange) {
      onActivedIndexChange(index);
    } else {
      setLocalActivedIndex(index);
    }
  }, [onActivedIndexChange]);

  const {
    paymentOpen,
    setPaymentPrice,
    getPaymentPrice,
  } = usePayment();
  const {
    value,
    fetch,
  } = usePaymentUpgrade();

  // 只在组件挂载时调用一次 fetch，避免 fetch 函数引用变化导致频繁执行
  useEffect(() => {
    if (!value?.items?.length) {
      fetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 空依赖数组，只在组件挂载时执行一次

  const prices = useMemo(() => {
    return value?.items?.map(({ price }) => price) || [];
  }, [value?.items]);

  const paymentPrice = getPaymentPrice();
  const paymentPriceRef = useRef(paymentPrice);
  const activedIndexRef = useRef(activedIndex);
  const isUpdatingFromPriceRef = useRef(false);
  const hasCalledOnPaymentOptionChangeRef = useRef(false);

  useEffect(() => {
    paymentPriceRef.current = paymentPrice;
  }, [paymentPrice]);

  useEffect(() => {
    activedIndexRef.current = activedIndex;
  }, [activedIndex]);

  // 当 paymentPrice 从外部变化时，同步 activedIndex（但避免循环）
  useEffect(() => {
    if (paymentPrice !== 0 && prices.length > 0 && value?.items) {
      const index = prices.findIndex((price) => price === paymentPrice);
      if (index !== -1) {
        const days = value.items[index]?.days || 0;
        // 如果 activedIndex 需要更新，则更新它
        if (index !== activedIndexRef.current) {
          isUpdatingFromPriceRef.current = true;
          hasCalledOnPaymentOptionChangeRef.current = false;
          setActivedIndex(index);
        } else if (!hasCalledOnPaymentOptionChangeRef.current) {
          // 如果 activedIndex 已经匹配，但可能 upgradePaymentOption 还没有设置，则直接调用 onPaymentOptionChange
          hasCalledOnPaymentOptionChangeRef.current = true;
          onPaymentOptionChange({ renewalDays: days, paymentPrice });
        }
      }
    }
  }, [paymentPrice, prices, value?.items, onPaymentOptionChange, setActivedIndex]);

  // 当 activedIndex 变化时，更新 paymentPrice（但避免从 paymentPrice 触发的循环）
  useEffect(() => {
    if (typeof activedIndex === 'undefined' || prices.length === 0) {
      return;
    }
    const price = prices[activedIndex];
    if (!price) return;

    const days = value?.items?.[activedIndex]?.days || 0;

    // 如果这次更新是从 paymentPrice 触发的，只调用 onPaymentOptionChange，不再次设置 paymentPrice
    if (isUpdatingFromPriceRef.current) {
      isUpdatingFromPriceRef.current = false;
      hasCalledOnPaymentOptionChangeRef.current = true;
      // 即使是从外部价格触发的，也需要调用 onPaymentOptionChange 来设置 upgradePaymentOption
      onPaymentOptionChange({ renewalDays: days, paymentPrice: price });
      return;
    }

    // 只有当价格真正变化时才更新
    if (paymentPriceRef.current !== price) {
      onPaymentOptionChange({ renewalDays: days, paymentPrice: price });
      setPaymentPrice(price);
    }
  }, [prices, activedIndex, setPaymentPrice, value?.items, onPaymentOptionChange, setActivedIndex]);

  useEffect(() => {
    if (paymentOpen === true && paymentPrice === 0) {
      setActivedIndex(0);
    }
  }, [paymentPrice, paymentOpen, setActivedIndex]);

  return (
    <>
      <div
        className="text-muted-foreground text-base text-center"
        style={{
          fontSize: `${16 * scale}px`,
          lineHeight: `${24 * scale}px`,
        }}
      >
        {value?.title}
      </div>
      <div
        className="flex flex-col"
        style={{
          padding: `${34 * scale}px ${32 * scale}px ${4 * scale}px ${60 * scale}px`,
          height: `${472 * scale}px`,
        }}
      >
        <div
          className="flex flex-row justify-center"
          style={{
            gap: `${12 * scale}px`,
          }}
        >
          {
            value?.items?.map(({
              label,
              price,
              priceDiscountedPerMonth,
              priceOriginalPerMonth,
              desc,
              badge,
            }, index) => {
              return (
                <div key={label}
                  className={cn(
                    "rounded-xl flex-1 h-fit relative cursor-pointer select-none",
                    index === activedIndex ?
                      "bg-gradient-to-r from-[#6A5035] to-[#8C7155]" :
                      "bg-transparent",
                  )}
                  style={
                    index === activedIndex ? {
                      padding: `${4 * scale}px`,
                    } : {
                      margin: `${2 * scale}px`,
                      padding: `${2 * scale}px`,
                    }
                  }
                  onClick={() => {
                    setActivedIndex(index);
                  }}
                >
                  {index === activedIndex && (
                    <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 flex flex-col items-center justify-center">
                      <VipPointerIcon width={34 * scale} height={20 * scale} />
                    </div>
                  )}

                  <div
                    className={cn(
                      "relative bg-background h-fit",
                      index === activedIndex ? "rounded-[6px]" : " rounded-[7px]"
                    )}
                    style={{
                      padding: `${36 * scale}px 0 ${36 * scale}px ${20 * scale}px`,
                    }}
                  >
                    <div
                      className="text-[#3D3D3D] font-semibold"
                      style={{
                        fontSize: `${24 * scale}px`,
                        lineHeight: `${32 * scale}px`,
                      }}
                    >
                      {label}
                    </div>
                    <div className="absolute right-0 top-0">
                      <PriceBadge label={desc} badge={badge as BadgeType} />
                    </div>
                    <div
                      className="font-semibold"
                      style={{
                        marginTop: `${16 * scale}px`,
                      }}
                    >
                      <MemberLabel
                        style={{
                          fontSize: `${24 * scale}px`,
                          lineHeight: `${32 * scale}px`,
                        }}
                      >¥</MemberLabel>
                      <MemberLabel
                        style={{
                          fontSize: `${44 * scale}px`,
                          lineHeight: 1,
                          marginLeft: `${4 * scale}px`,
                        }}
                      >{price}</MemberLabel>
                      <div
                        className="text-annotation-foreground inline-flex flex-wrap font-normal"
                        style={{
                          marginLeft: `${8 * scale}px`,
                          gap: `${8 * scale}px`,
                          fontSize: `${12 * scale}px`,
                          lineHeight: `${16 * scale}px`,
                        }}
                      >
                        <span>¥{priceDiscountedPerMonth}/月</span>
                        <span><span className="line-through">¥{priceOriginalPerMonth}</span>/月</span>
                      </div>
                    </div>

                  </div>
                </div>
              )
            })
          }
        </div>
        <div
          className={cn(
            "rounded-xl flex-1",
            "bg-gradient-to-r from-[#6A5035] to-[#8C7155]",
          )}
          style={{
            padding: `${4 * scale}px`,
            marginTop: `${24 * scale}px`
          }}

        >
          <div
            className={cn(
              "relative bg-background h-full flex flex-col justify-center ",
              "rounded-[6px]"
            )}
            style={{
              padding: `${24 * scale}px`,
              paddingRight: `${8 * scale}px`,
            }}
          >
            <PaymentRightsList
              scale={scale}
              style={{
                marginLeft: `${6 * scale}px`,
              }}
              items={value?.items?.[activedIndex || 0]?.rightsList as PaymentRightsItem[]}
              columns={3}
            />
          </div>
        </div>
        <div
          className="text-end text-annotation-foreground"
          style={{
            fontSize: `${14 * scale}px`,
            lineHeight: `${20 * scale}px`,
            marginTop: `${20 * scale}px`,
            marginBottom: `${-30 * scale}px`,
          }}
        >
          Plus 会员时长每月为 31 天。
        </div>
      </div>
    </>
  )
}